import Dexie from 'dexie'

const db = new Dexie('wondergrip_db')

db.version(1).stores({
  collections: '++id, name, data, updatedAt, userId', // Heavy data
  metadata: '++id, name, userId, estimatedSize, recordCount, updatedAt', // Lightweight metadata only
})

// Helper to get user cache key
const getUserCacheKey = (user) => {
  if (!user) return 'anonymous'
  return user.email || user.id || 'unknown'
}

// Accurate size calculation (only done once during save, so we can afford precision)
const calculateDataSize = (data) => {
  if (!data) return 0

  // Simple and reliable: just stringify and measure
  // We can afford this since it's only done once per collection save
  try {
    const jsonString = JSON.stringify(data)
    const sizeInBytes = jsonString.length * 2 // UTF-16 encoding

    // Apply the 0.6 correction factor for realistic estimation
    return Math.round(sizeInBytes * 0.6)
  } catch (error) {
    console.warn('Failed to calculate data size, using fallback estimation:', error)

    // Fallback for circular references or non-serializable data
    if (Array.isArray(data)) {
      return data.length * 1000 * 0.6 // ~1KB per item estimate
    }

    if (typeof data === 'object') {
      return Object.keys(data).length * 500 * 0.6 // ~500B per property estimate
    }

    return 100 // Small default
  }
}

export const loadCollections = async (user) => {
  const userId = getUserCacheKey(user)
  console.log(`📦 Loading collections for user: ${userId}`)

  try {
    const records = await db.collections.where('userId').equals(userId).toArray()

    if (records.length === 0) {
      console.log(`📦 No cached collections found for user: ${userId}`)
      return null
    }

    // Convert array back to object format
    const collections = {}
    records.forEach(record => {
      collections[record.name] = record.data
    })

    console.log(`📦 Loaded cached collections for user ${userId}:`, Object.keys(collections))
    return collections
  } catch (error) {
    console.error(`❌ Error loading collections for user ${userId}:`, error)
    return null
  }
}

export const saveCollections = async (collections, user) => {
  const userId = getUserCacheKey(user)
  console.log(`💾 Saving collections for user: ${userId}`)

  try {
    // Clear existing collections for this user
    await db.collections.where('userId').equals(userId).delete()
    await db.metadata.where('userId').equals(userId).delete()

    // Prepare records for both tables
    const collectionRecords = []
    const metadataRecords = []

    Object.entries(collections).forEach(([name, data]) => {
      const estimatedSize = calculateDataSize(data)
      const recordCount = Array.isArray(data) ? data.length : 1
      const updatedAt = new Date().toISOString()

      // Heavy data table
      collectionRecords.push({
        name,
        data,
        userId,
        updatedAt,
      })

      // Lightweight metadata table
      metadataRecords.push({
        name,
        userId,
        estimatedSize,
        recordCount,
        updatedAt,
      })
    })

    // Save to both tables
    await db.collections.bulkAdd(collectionRecords)
    await db.metadata.bulkAdd(metadataRecords)

    console.log(`💾 Saved ${collectionRecords.length} collections for user ${userId}`)
  } catch (error) {
    console.error(`❌ Error saving collections for user ${userId}:`, error)
  }
}

export const clearCollections = async (user = null) => {
  try {
    if (user) {
      const userId = getUserCacheKey(user)
      console.log(`🗑️ Clearing collections for user: ${userId}`)
      await db.collections.where('userId').equals(userId).delete()
      await db.metadata.where('userId').equals(userId).delete()
    } else {
      console.log('🗑️ Clearing all collections')
      await db.collections.clear()
      await db.metadata.clear()
    }
  } catch (error) {
    console.error('❌ Error clearing collections:', error)
  }
}

// Super fast cache stats using dedicated metadata table
export const getCacheStats = async () => {
  try {
    console.log('📊 Getting cache stats from metadata table...')

    // INSTANT: Load from lightweight metadata table only
    const metadataRecords = await db.metadata.toArray()

    if (metadataRecords.length === 0) {
      return {
        totalSize: 0,
        totalRecords: 0,
        analyzedRecords: 0,
        wasTruncated: false,
        userStats: {},
      }
    }

    // Lightning-fast aggregation
    const userStats = {}
    let totalSize = 0

    metadataRecords.forEach(record => {
      const userId = record.userId || 'unknown'
      const size = record.estimatedSize || 0
      const count = record.recordCount || 0

      if (!userStats[userId]) {
        userStats[userId] = {
          collections: 0,
          size: 0,
          recordCount: 0,
          lastUpdated: record.updatedAt,
        }
      }

      userStats[userId].collections += 1
      userStats[userId].size += size
      userStats[userId].recordCount += count
      totalSize += size

      // Keep most recent update time
      if (record.updatedAt > userStats[userId].lastUpdated) {
        userStats[userId].lastUpdated = record.updatedAt
      }
    })

    console.log(`📊 Cache stats computed INSTANTLY from metadata table (${metadataRecords.length} collections)`)

    return {
      totalSize,
      totalRecords: metadataRecords.length,
      analyzedRecords: metadataRecords.length,
      wasTruncated: false,
      userStats,
    }
  } catch (error) {
    console.error('❌ Error getting cache stats:', error)
    return {
      totalSize: 0,
      totalRecords: 0,
      analyzedRecords: 0,
      wasTruncated: false,
      userStats: {},
      error: error.message,
    }
  }
}

export const clearAllCache = async () => {
  try {
    await db.collections.clear()
    await db.metadata.clear() // Clear the new metadata table
    console.log('🗑️ All cache cleared')
  } catch (error) {
    console.error('❌ Error clearing all cache:', error)
  }
}

export default db